import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getGatePassDetails,
    updateGatePass,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_GATE_PASSES } from '../../../../Routing/routes';
import GatePassForm from './GatePassForm';
import VisitorNotFound from '../components/VisitorNotFound';
import { VmsLoader } from '../components/vms/VmsListShell';
import { toDateInput, flatLabel } from '../vmsConstants';
import { fmtPhone } from '../visitorConstants';

const EditGatePass = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { details, detailsLoading, saving } = useSelector(state => state.VmsReducer?.gatePasses) || {};

    useEffect(() => {
        if (id) dispatch(getGatePassDetails({ id }));
    }, [dispatch, id]);

    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_GATE_PASSES);

    const initial = useMemo(() => {
        if (!details) return null;
        const who = details.visitor
            ? `${details.visitor.name} · ${fmtPhone(details.visitor.phone)}`
            : `Visitor #${details.visitor_id}`;
        return {
            pass_type: details.pass_type || 'TEMPORARY',
            purpose: details.purpose || '',
            valid_from: toDateInput(details.valid_from),
            valid_until: toDateInput(details.valid_until),
            allowed_days: Array.isArray(details.allowed_days) ? details.allowed_days : [],
            // Stored as HH:MM:SS; <input type="time"> wants HH:MM.
            allowed_time_from: (details.allowed_time_from || '').slice(0, 5),
            allowed_time_to: (details.allowed_time_to || '').slice(0, 5),
            max_entries: details.max_entries ?? '',
            vehicle_number: details.vehicle_number || '',
            vehicle_type: details.vehicle_type || '',
            remarks: details.remarks || '',
            visitorLabel: `${who} · ${flatLabel(details)}`,
        };
    }, [details]);

    const handleSubmit = (payload) => {
        dispatch(updateGatePass({ id, ...payload, onSuccess: backToList }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-w-full">
                <button
                    onClick={backToList}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 tw-mb-3"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Gate Passes
                </button>

                {detailsLoading && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-py-10">
                        <VmsLoader label="Loading gate pass..." />
                    </div>
                )}

                {!detailsLoading && !details && <VisitorNotFound onBack={backToList} />}

                {!detailsLoading && details && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100">
                            <h1 className="tw-mb-0 tw-text-base tw-font-bold tw-text-gray-800">Edit Gate Pass</h1>
                            <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                <span className="tw-font-mono">{details.pass_code}</span> · used {details.used_entries ?? 0}
                                {details.max_entries ? ` of ${details.max_entries}` : ''} times
                            </p>
                        </div>
                        <div className="tw-p-5">
                            {details.status !== 'ACTIVE' && (
                                <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2 tw-mb-4 tw-rounded-md tw-bg-amber-50 tw-border tw-border-amber-100 tw-text-[11px] tw-text-amber-800">
                                    <Icon icon="mdi:alert-outline" className="tw-text-sm tw-shrink-0 tw-mt-px" />
                                    <span>
                                        This pass is {details.status.toLowerCase()}. Editing its rules will not make it
                                        work again — a revoked pass cannot be reactivated, so issue a new one instead.
                                    </span>
                                </div>
                            )}
                            <GatePassForm
                                mode="edit"
                                societyId={societyId}
                                initial={initial}
                                submitting={saving}
                                onSubmit={handleSubmit}
                                onCancel={backToList}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditGatePass;
